import { sha256 } from "js-sha256";
import { extractQuestionProps } from "src/scenes/parent/forms/components/Inputs/FormQuestion";
import { CustomQuestionAnswersByQuestionId } from "src/schemas/CustomQuestionType";
import * as AF from "src/types/formTemplate";
import { Applicant } from "src/types/formTemplate/formTemplateFilters";
import { CloneIdsGroupedBySourceIds } from "./context/helpers";
import { AnswerBankRecordField } from "./list/schemas/AnswerBankField";

// AnswerBankRecordField[] > CustomQuestionAnswerByQuestionId (form answers)
export function transformAnswerBankFieldsToValues(
  fields: AnswerBankRecordField[]
): CustomQuestionAnswersByQuestionId {
  return fields.reduce<Record<uuid, string>>((accumulator, currentField) => {
    const { clonedQuestionId, questionType } = currentField;

    switch (questionType) {
      case AF.FreeTextType:
      case AF.EmailType:
      case AF.PhoneNumberType:
      case AF.DateType:
      case AF.NumberType:
        // TODO: handle date and number type
        accumulator[clonedQuestionId] = currentField.answer;
        break;
      case AF.SingleSelectType:
        accumulator[clonedQuestionId] =
          currentField.clonedQuestionSelectOptionId ?? "";
        break;
      default: {
        const _exhaustiveCheck: never = questionType;
        return _exhaustiveCheck;
      }
    }

    return accumulator;
  }, {});
}

// CustomQuestionAnswerByQuestionId (form answers) > AnswerBankRecordField[]
export function transformValuesIntoAnswerBankRecordFields(
  answersByQuestionId: CustomQuestionAnswersByQuestionId,
  nestedQuestions: AF.ClonedQuestion<AF.WithId>[],
  sourceIdCloneIdMapping: CloneIdsGroupedBySourceIds
): AnswerBankRecordField[] {
  return nestedQuestions.map((clonedQuestion) => {
    const { id, question, type } = clonedQuestion;
    const field = Object.entries(sourceIdCloneIdMapping).find(
      ([_, value]) => value.clonedQuestionId === id
    );

    if (!field) {
      throw new Error(`Could not find field for ${id}`);
    }

    const commonProperties = {
      questionLabel: question,
      clonedQuestionId: id,
      customQuestionTypeFieldId: field[0],
    };

    switch (type) {
      case AF.FreeTextType:
      case AF.EmailType:
      case AF.PhoneNumberType:
      case AF.DateType:
      case AF.NumberType:
        // TODO: handle date and number type
        return {
          ...commonProperties,
          questionType: type,
          answer: answersByQuestionId[id] ?? "",
        };
      case AF.SingleSelectType:
        const selectOption =
          type === AF.SingleSelectType
            ? clonedQuestion.options.find((option) => {
                return option.id === answersByQuestionId[id];
              })
            : undefined;

        return {
          ...commonProperties,
          questionType: type,
          clonedQuestionSelectOptionId: answersByQuestionId[id] ?? "",
          clonedQuestionSelectOptionLabel: selectOption?.label ?? "",
        };
      default: {
        const _exhaustiveCheck: never = type;
        return _exhaustiveCheck;
      }
    }
  });
}

// answer bank answers > form answers
export function mapBankAnswersToFormAnswers(
  answerBankValues: CustomQuestionAnswersByQuestionId,
  mapping: CloneIdsGroupedBySourceIds
): CustomQuestionAnswersByQuestionId {
  const mappedFormikValues = Object.entries(answerBankValues).reduce(
    (accumulator, currentAnswerBankValue) => {
      const [customQuestionTypeFieldId, answer] = currentAnswerBankValue;
      const clonedQuestion = mapping[customQuestionTypeFieldId];
      if (!clonedQuestion) {
        throw new Error(
          `Could not find cloned question mapping for sourceId ${customQuestionTypeFieldId}`
        );
      }
      const { clonedQuestionId, optionIdMapping } = clonedQuestion;
      const clonedOptionId = optionIdMapping?.[answer];
      const formAnswer = clonedOptionId ? clonedOptionId : answer;

      accumulator.set(clonedQuestionId, formAnswer);

      return accumulator;
    },
    new Map()
  );

  return Object.fromEntries(mappedFormikValues);
}

// form answers > answer bank answers
export function mapFormAnswersToBankAnswers(
  formAnswers: CustomQuestionAnswersByQuestionId,
  mapping: CloneIdsGroupedBySourceIds
): CustomQuestionAnswersByQuestionId {
  const answerBankAnswers = Object.entries(formAnswers).reduce(
    (accumulator, currentFormAnswer) => {
      const [formQuestionId, answer] = currentFormAnswer;
      const customQuestionTypeField = Object.entries(mapping).find(
        ([_, value]) => value.clonedQuestionId === formQuestionId
      );
      if (!customQuestionTypeField) {
        throw new Error(
          `Could not find custom question type field for cloneId ${formQuestionId}`
        );
      }
      const [fieldQuestionId, { optionIdMapping }] = customQuestionTypeField;
      const fieldOptionEntry = optionIdMapping
        ? Object.entries(optionIdMapping).find(
            ([_, clonedOptionId]) => clonedOptionId === answer
          )
        : undefined;
      const answerBankAnswer = fieldOptionEntry ? fieldOptionEntry[0] : answer;

      accumulator.set(fieldQuestionId, answerBankAnswer);

      return accumulator;
    },
    new Map()
  );

  return Object.fromEntries(answerBankAnswers);
}

// custom question type field > question props
type FormatAvelaQuestionAsProperties = {
  applicant: Applicant;
  formId: uuid;
  fieldQuestions: AF.ClonedQuestion<AF.WithId>[];
  disableDocumentLibrary: boolean;
};
export function formatCustomQuestionTypeFieldAsProperties(
  props: FormatAvelaQuestionAsProperties
) {
  const { applicant, formId, fieldQuestions, disableDocumentLibrary } = props;

  return fieldQuestions.map((question) => {
    return extractQuestionProps(
      question,
      formId,
      applicant,
      false,
      disableDocumentLibrary
    );
  });
}

// This is the same logic as in apply-api form-import lambda
export function computeDigestFromQuestionsAndAnswers(
  fieldQuestions: AF.ClonedQuestion<AF.WithId>[],
  answersByQuestionId: CustomQuestionAnswersByQuestionId
): string {
  const answersList = fieldQuestions.map((question) => {
    const answerValue = answersByQuestionId[question.id] ?? "";

    // For SingleSelect fields, use the option label instead of the option ID
    if (question.type === AF.SingleSelectType && answerValue) {
      const singleSelectQuestion = question as AF.SingleSelect<AF.WithId>;
      const option = singleSelectQuestion.options?.find(
        (opt) => opt.id === answerValue
      );
      return option?.label ?? "";
    }

    // For all other field types, use the answer value directly
    return answerValue;
  });
  const message = answersList.join(",");
  return sha256(message);
}

export function computeDigestFromAnswerBankFields(
  answerBankRecordFields: AnswerBankRecordField[]
): string {
  const answersList = answerBankRecordFields.map((field) => {
    return field.questionType === AF.SingleSelectType
      ? field.customQuestionTypeFieldSelectOptionLabel
      : field.answer;
  });
  return sha256(answersList.join(","));
}

// special cased logic for SAISD based on the CQT field
// These fields cannot be edited by parents after creation
export const IDENTIFYING_CONTACT_FIELDS = ["first_name", "last_name"];
